import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, ChevronDown, ChevronRight, Camera, FileText } from 'lucide-react';
import EyeAILogo from '../images/EyeAILogo.jpg';
import { useProfile } from '../contexts/ProfileContext';

const HomeIcon = (props) => (
  <svg width="18px" height="18px" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-house-fill" viewBox="0 0 16 16">
    <path fillRule="evenodd" d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6zm5-.793V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"></path>
    <path fillRule="evenodd" d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"></path>
  </svg>
);

const OperateIcon = (props) => (
  <svg width="18px" height="18px" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-person-video2" viewBox="0 0 16 16">
    <path d="M10 9.05a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"></path>
    <path d="M2 1a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2H2ZM1 3a1 1 0 0 1 1-1h2v2H1V3Zm4 10V2h9a1 1 0 0 1 1 1v9c0 .285-.12.543-.31.725C14.15 11.494 12.822 10 10 10c-3.037 0-4.345 1.73-4.798 3H5Zm-4-2h3v2H2a1 1 0 0 1-1-1v-1Zm3-1H1V8h3v2Zm0-3H1V5h3v2Z"></path>
  </svg>
);

const EquipmentIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-camera-icon lucide-camera">
    <path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z"/>
    <circle cx="12" cy="13" r="3"/>
  </svg>
);

const ReportIcon = (props) => (
  <svg width="18px" height="18px" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-house-fill" viewBox="0 0 16 16">
    <path fillRule="evenodd" d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6zm5-.793V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"></path>
    <path fillRule="evenodd" d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"></path>
  </svg>
);

// Sidebar Component
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [usersExpanded, setUsersExpanded] = useState(true);
  const { profile } = useProfile();

  const menuItems = [
    { id: 'patients', icon: Users, label: 'Patients', paths: ['/', '/patient-detail'], userGroups: ['DEVICE'] },
    { id: 'patients-opr', icon: Users, label: 'Patients', paths: ['/patient', '/patient-detail'], userGroups: ['OPERATOR'] },
  
    { id: 'operators', icon: OperateIcon, label: 'Operators', paths: ['/operators', '/operator-detail', '/operator-topup'], userGroups: ['CDP'] },
    { id: 'equipment', icon: Camera, label: 'Equipment List', paths: ['/equipment', '/equipment-detail'], userGroups: ['ADMIN', 'CDP'] },
    { id: 'device', icon: Camera, label: 'Device', paths: ['/equipment', '/equipment-detail'], userGroups: ['OPERATOR'] },
    // { id: 'device', icon: Camera, label: 'Device', paths: ['/device'], userGroups: ['OPERATOR'] },
    { id: 'report', icon: FileText, label: 'CDP Usage Report', paths: ['/report'], userGroups: ['ADMIN', 'DISTRIBUTOR'] },
    { id: 'opreport', icon: FileText, label: 'Usage Report', paths: ['/opreport'], userGroups: ['CDP', 'OPERATOR'] },
    { id: 'devicereport', icon: FileText, label: 'Patient Report', paths: ['/devicereport'], userGroups: ['DEVICE'] },
  ];

  const userSubMenu = [
    { id: 'country-partners', label: 'Country Partners', paths: ['/users/country-partners', '/users/cdp-detail', '/users/cdp-topup'], userGroups: ['ADMIN'] },
    { id: 'distributors', label: 'Distributors', paths: ['/users/distributors', '/users/distributor-detail'], userGroups: ['ADMIN'] },
    { id: 'administrators', label: 'Administrators', paths: ['/users/administrators', '/users/admin-detail'], userGroups: ['ADMIN'] },
  ];

  const isPathActive = (paths) => {
    return paths.includes(location.pathname);
  }

  const isUserMenuActive = userSubMenu.some(item => isPathActive(item.paths));
 
  return (
    <div className="w-[220px] bg-white border-r border-gray-200 h-full flex flex-col">
      <div className="flex items-center justify-center h-20">
        <img src={EyeAILogo} alt="EyeAI Logo" className="w-[160px] h-[60px]"/>
      </div>

      {/* Navigation */}
      <nav className="p-4 flex-1">
      
      <button
            key={0}
            onClick={() => navigate('/dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
              isPathActive(['/dashboard'])
                ? 'bg-gray-100 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <HomeIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Dashboard</span>
      </button>

      {profile && profile.userGroup === 'ADMIN' && (
        <div className="mb-2">
          <button
            onClick={() => setUsersExpanded(!usersExpanded)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isUserMenuActive
                ? 'bg-gray-100 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-5 h-5" />
            <span className="text-sm font-medium flex-1 text-left">Users</span>
            {usersExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {/* Submenu */}
          {usersExpanded && (
            <div className="ml-4 mt-1">
              {userSubMenu.map((subItem) => (
                subItem.userGroups.includes(profile.userGroup) && <button
                  key={subItem.id}
                  onClick={() => navigate(subItem.paths[0])}
                  className={`w-full text-left items-center gap-3 px-4 py-2 rounded-lg mb-1 transition-colors ${
                    isPathActive(subItem.paths)
                      ? 'bg-gray-100 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm">{subItem.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>)}

      {profile && menuItems.map((item) => (
        item.userGroups.includes(profile.userGroup) && (
          <button 
            key={item.id}
            onClick={() => navigate(item.paths[0])}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
              isPathActive(item.paths)
                ? 'bg-gray-100 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-sm font-medium">{item.label}</span>
          </button>)
      ))}


      {profile && profile.userGroup === 'ADMIN' && (
        <>
          {/* INFO Section */}
          <div className="mt-6 mb-2">
            <div className="px-4 py-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Info
              </span>
            </div>
            <button
              onClick={() => navigate('/changelog')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isPathActive(['/changelog'])
                  ? 'bg-gray-100 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <svg width="18px" height="18px" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M8.646 5.646a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L10.293 8 8.646 6.354a.5.5 0 0 1 0-.708zm-1.292 0a.5.5 0 0 0-.708 0l-2 2a.5.5 0 0 0 0 .708l2 2a.5.5 0 0 0 .708-.708L5.707 8l1.647-1.646a.5.5 0 0 0 0-.708z"></path><path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2z"></path><path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1z"></path>
              </svg>
              <span className="text-sm font-medium">Changelog</span>
            </button>
          </div>
        </>
      )} 
      </nav>
      
      {/* Help Button */}
      <div className="p-6">
        <button className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
          <svg className="text-gray-600" width="22px" height="22px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512.052 512.052" fill="currentColor">
            <path d="M208.026,16.026c-114.7,0-208,78.9-208,176c0,39.8,15.7,77.9,44.5,108.9l-39.8,39.8c-4.6,4.6-6,11.5-3.5,17.4c2.5,6,8.3,9.9,14.8,9.9h192c114.7,0,208-78.9,208-176S322.726,16.026,208.026,16.026z"></path>
            <path opacity="0.5" d="M467.526,428.926c28.8-30.9,44.5-69.1,44.5-108.9c0-49.8-24.6-94.9-64-126.9c-0.9,114.1-108.2,206.9-240,206.9h-89.2c34.5,56.9,104.6,96,185.2,96h192c6.5,0,12.3-3.9,14.8-9.9c2.5-6,1.1-12.9-3.5-17.4L467.526,428.926z"></path>
            <path fill="#ffffff" d="M304.026,144.026h-192c-8.8,0-16,7.2-16,16s7.2,16,16,16h192c8.8,0,16-7.2,16-16S312.826,144.026,304.026,144.026z"></path>
            <path fill="#ffffff" d="M240.026,208.026h-128c-8.8,0-16,7.2-16,16s7.2,16,16,16h128c8.8,0,16-7.2,16-16S248.826,208.026,240.026,208.026z"></path>
          </svg>
          <span className="text-sm">Need help?</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;